import React, { useState } from 'react';
import { Wand2, Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { capRefine } from '../../lib/capstoneApi';

/**
 * "Refine to CXO-ready" bar — rewrites the user's rough inputs into professional sentences.
 * Props:
 *  - getFields(): {key: currentText} of the fields on this screen
 *  - context: {title, domain, ...} light project context
 *  - onApply(refinedFields): apply refined values into state
 *  - label: optional custom label
 */
export const RefineBar = ({ getFields, context, onApply, label }) => {
  const [loading, setLoading] = useState(false);

  const refine = async () => {
    const fields = getFields() || {};
    const nonEmpty = Object.fromEntries(Object.entries(fields).filter(([, v]) => (v || '').trim()));
    if (!Object.keys(nonEmpty).length) {
      toast.info('Nothing to refine yet — jot down a rough draft first, then refine.');
      return;
    }
    setLoading(true);
    try {
      const res = await capRefine(nonEmpty, context || {});
      const previous = { ...nonEmpty };
      onApply(res.fields || {});
      toast.success('Refined to professional, CXO-ready language — review and adjust.', {
        duration: 8000,
        action: { label: 'Undo', onClick: () => onApply(previous) },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refine failed — try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-teal-400/25 bg-teal-400/[0.05] px-4 py-3 mb-5" data-testid="cap-refine-bar">
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold text-teal-300 flex items-center gap-1.5">
          <Wand2 className="w-3.5 h-3.5" /> {label || 'Make my inputs CXO-ready'}
        </div>
        <div className="text-[10.5px] text-white/40 mt-0.5">Guide rewrites what you've typed into crisp professional sentences — facts stay untouched, undo anytime.</div>
      </div>
      <button
        type="button"
        onClick={refine}
        disabled={loading}
        data-testid="cap-refine-btn"
        className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-teal-300/50 text-teal-200 text-[12px] font-bold hover:bg-teal-300/10 transition-colors disabled:opacity-40"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        {loading ? 'Refining…' : 'Refine'}
      </button>
    </div>
  );
};

export default RefineBar;
